import { CartItem, ProductCartItem } from '@lib/cartState/types';
import { productVariantItem, BundleItem, freeProduct } from '@lib/productsState/productsTypes';
import { useState, useEffect } from 'react';

export const calculateMainTotals = (items: CartItem[]) => {
  const mainTotal = items.reduce((acc, product) => {
    const productPrice = Number(product.price) * Number(product.amount);
    return acc + productPrice;
  }, 0);

  const mainSavings = items.reduce((acc, product) => {
    const oldPrice = Number(product.old_price) * Number(product.amount);
    const newPrice = Number(product.price) * Number(product.amount);
    return acc + (oldPrice - newPrice);
  }, 0);

  return { mainTotal, mainSavings };
};

export const useGroupProductsByVariants = (cartProducts: ProductCartItem[]) => {
  const [groupedProducts, setGroupedProducts] = useState<ProductCartItem[]>([]);

  useEffect(() => {
    const newProductsArray: ProductCartItem[] = [];

    cartProducts.forEach((item) => {
      if (item?.variants && item?.variants?.length > 1) {
        const variantGroups: Record<
          string,
          {
            variant: productVariantItem;
            count: number;
          }
        > = {};

        item.variants &&
          item.variants?.forEach((variant: productVariantItem) => {
            const variantKey = `${variant?.color || ''}_${variant?.size || ''}`;

            if (!variantGroups[variantKey]) {
              variantGroups[variantKey] = {
                variant,
                count: 0,
              };
            }

            variantGroups[variantKey].count++;
          });

        Object.values(variantGroups).forEach(({ variant, count }) => {
          newProductsArray.push({
            ...item,
            amount: Number(count),
            variants: [variant],
            sku: `${item.sku}_${variant?.color || ''}_${variant?.size || ''}`,
          });
        });
      } else {
        newProductsArray.push(item);
      }
    });

    setGroupedProducts(newProductsArray);
  }, [cartProducts]);

  return groupedProducts;
};

export const recalcProductsQuantity = (cartProducts: ProductCartItem[]) => {
  return cartProducts.reduce((total, item) => Number(total) + Number(item.amount), 0);
};

export const isEligibleForFreeItem = (cartProducts: ProductCartItem[], id: string, freeItemsStep: number): boolean => {
  const paidItemsCount = cartProducts.find((product) => product.id === id)?.amount || 0;
  const freeItemsCount = cartProducts.find((product) => product.id === `${id}_free`)?.amount || 0;

  console.log(paidItemsCount, freeItemsCount);
  const eligibleFreeItems = Math.floor(paidItemsCount / (freeItemsStep - 1));

  return freeItemsCount < eligibleFreeItems;
};

export const calcEligibleFreeItemsCount = (amount: number, freeItemStep: number): number => {
  if (!freeItemStep || freeItemStep <= 0) return 0;
  return Math.floor((amount - 1) / freeItemStep);
};

export const getEligibleFreeItemsForProduct = (
  cartProducts: ProductCartItem[],
  id: string,
  freeItemStep: number
): number => {
  const paidItemsCount = cartProducts.find((product) => product.id === id)?.amount || 0;
  console.log(paidItemsCount);
  return calcEligibleFreeItemsCount(paidItemsCount, freeItemStep);
};

export const updateExistingCartItem = (
  cartProducts: ProductCartItem[],
  itemId: string,
  amountToAdd: number,
  variantsToApply: productVariantItem[]
) => {
  const index = cartProducts.findIndex((item) => item.id === itemId);
  console.log(itemId);
  if (index !== -1) {
    cartProducts[index].amount += Number(amountToAdd);
    if (Array.isArray(cartProducts[index].variants) && Array.isArray(variantsToApply)) {
      cartProducts[index].variants = [...cartProducts[index].variants, ...variantsToApply];
    } else if (variantsToApply) {
      cartProducts[index].variants = { ...cartProducts[index].variants, ...variantsToApply };
    }
    return true;
  }
  return false;
};

export const addFreeProductToCart = (
  cartProducts: ProductCartItem[],
  product: ProductCartItem,
  freeProduct: freeProduct,
  variantsToApply: productVariantItem[]
) => {
  cartProducts.push({
    id: `${product.id}_free`,
    parentId: product.id,
    sku: freeProduct.fp_sku,
    amount: Number(freeProduct.fp_amount) ?? product.amount,
    konnektive_id: freeProduct.fp_konnektive_id,
    price: 0,
    old_price: 0,
    shipping_price: 0,
    status: product.status,
    description: product.description,
    display_name: freeProduct.display_name ?? product.display_name,
    generic_name: freeProduct.generic_name ?? product.generic_name,
    free_product_step: product.free_product_step,
    image: product.image,
    variants: variantsToApply,
    bundle_products: [],
    free_products: [],
    type: 'free',
  });
};

export const addProductToCart = (
  cartProducts: ProductCartItem[],
  product: ProductCartItem,
  variantsToApply: productVariantItem[]
) => {
  cartProducts.push({
    id: `${product.id}_free`,
    parentId: product.id,
    sku: product.sku,
    amount: 1,
    price: 0,
    old_price: 0,
    shipping_price: 0,
    status: product.status,
    konnektive_id: product.konnektive_id,
    description: product.description,
    display_name: product.display_name,
    generic_name: product.generic_name,
    free_product_step: product.free_product_step,
    image: product.image,
    variants: variantsToApply,
    bundle_products: [],
    free_products: [],
    type: 'free',
  });
};

export const addBundleProductToCart = (
  cartProducts: ProductCartItem[],
  product: ProductCartItem,
  variantsToApply: productVariantItem[]
) => {
  cartProducts.push({
    id: product.id,
    sku: product.sku,
    amount: Number(product.amount),
    price: Number(product.price),
    old_price: Number(product.old_price),
    display_name: product.display_name,
    generic_name: product.generic_name,
    konnektive_id: product.konnektive_id,
    shipping_price: 0,
    free_product_step: product.free_product_step,
    status: product.status,
    description: product.description,
    image: product.image,
    variants: variantsToApply,
    free_products: [],
    type: 'offer',
  });
};
